// Botón `?` que abre un modal con la explicación de una sección.
// Se usa desde SectionHeader vía el prop helpId. El contenido vive
// en src/lib/helpContent.ts.
//
// IMPORTANTE: el modal se renderiza con createPortal a document.body.
// Sin esto, como SectionHeader tiene transform/overflow-hidden, el
// `position: fixed` del modal se ancla al header (no al viewport) y
// aparece cortado/detrás. El portal lo saca al body y cubre toda la
// pantalla correctamente.

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, X, PlayCircle, Check, AlertTriangle } from 'lucide-react';
import { HELP_CONTENT } from '@/lib/helpContent';

export function HelpHint({ helpId }: { helpId: string }) {
  const entry = HELP_CONTENT[helpId];
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    // Bloquear scroll del fondo mientras el modal está abierto.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!entry) return null;

  const modal = open ? createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-card border-2 border-accent/40 rounded-2xl max-w-sm w-full p-6 space-y-4 shadow-2xl shadow-accent/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center shrink-0">
              <HelpCircle className="w-5 h-5 text-accent" />
            </div>
            <h2 className="text-lg font-black uppercase tracking-tight text-foreground leading-tight">
              {entry.title}
            </h2>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="shrink-0 text-muted-foreground hover:text-foreground p-1 -mr-1 -mt-1"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed">
          {entry.body}
        </p>

        {entry.actions && entry.actions.length > 0 && (
          <div className="space-y-2">
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              Qué puedes hacer aquí
            </div>
            <ul className="space-y-1.5">
              {entry.actions.map((a, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                  <Check className="w-3.5 h-3.5 text-accent shrink-0 mt-0.5" />
                  <span className="leading-snug">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {entry.emphasis && entry.emphasis.length > 0 && (
          <div className="rounded-xl bg-accent/10 border border-accent/40 p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-accent">
              <AlertTriangle className="w-3.5 h-3.5" />
              Importante
            </div>
            {entry.emphasis.map((e, i) => (
              <p key={i} className="text-xs text-foreground/90 leading-relaxed">
                {e}
              </p>
            ))}
          </div>
        )}

        {entry.tutorialId && (
          <button
            onClick={() => {
              setOpen(false);
              navigate(`/soporte?tutorial=${entry.tutorialId}`);
            }}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground font-black text-xs uppercase tracking-widest min-h-[44px]"
          >
            <PlayCircle className="w-4 h-4" />
            Ver tutorial en video
          </button>
        )}

        <button
          onClick={() => setOpen(false)}
          className="w-full text-center text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground py-2 transition-colors"
        >
          Entendido
        </button>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      {/* Botón `?` sólido con glow — contrasta fuerte vs el fondo
          para que se note que es ayuda. Sin ping continuo (10 a la
          vez sería ruido); el color sólido + glow ya destaca. */}
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center shadow-lg shadow-accent/50 ring-2 ring-accent/30 hover:scale-110 active:scale-95 transition-transform"
        aria-label={`¿Qué es ${entry.title}? — Ayuda`}
        title={`¿Qué es ${entry.title}?`}
      >
        <HelpCircle className="w-5 h-5 md:w-6 md:h-6" />
      </button>
      {modal}
    </>
  );
}
